import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { getProfileImageWithFallback } from '../../utils/profileImageUtils';
import RandomGirlsModule from './RandomGirlsModule';

const BookedGirlDisplay = ({ tripPlanId, requiredGirls = 1 }) => {
  const [bookedGirls, setBookedGirls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingStats, setBookingStats] = useState({
    confirmed: 0,
    total: 0,
    remaining: 0
  });

  useEffect(() => {
    if (tripPlanId) {
      fetchBookedGirls();
    }
  }, [tripPlanId]);


  const fetchBookedGirls = async () => {
    try {
      setLoading(true);
      
      // ดึงข้อมูลการจองที่เกี่ยวข้องกับ trip plan นี้
      const bookingsRef = collection(db, 'bookings');
      const q = query(
        bookingsRef,
        where('tripPlanId', '==', tripPlanId),
        where('status', 'in', ['confirmed', 'completed'])
      );
      
      const querySnapshot = await getDocs(q);
      const girls = [];
      const uniqueGirlIds = new Set(); // ใช้ Set เพื่อป้องกันการซ้ำ
      
      for (const bookingDoc of querySnapshot.docs) {
        const bookingData = bookingDoc.data();
        
        // ตรวจสอบว่าเป็นน้องคนใหม่หรือไม่
        if (!uniqueGirlIds.has(bookingData.girlId)) {
          uniqueGirlIds.add(bookingData.girlId);
          
          // ดึงข้อมูลน้องจาก girls collection
          const girlRef = doc(db, 'girls', bookingData.girlId);
          const girlSnap = await getDoc(girlRef);
          
          if (girlSnap.exists()) {
            const girlData = girlSnap.data();
            girls.push({
              id: girlData.id || girlSnap.id,
              nickname: girlData.nickname,
              image: getProfileImageWithFallback(girlData, '/default-avatar.png'),
              status: bookingData.status,
              bookingDate: bookingData.bookingDate,
              bookingTime: bookingData.bookingTime
            });
          }
        }
      }
      
      setBookedGirls(girls);
      
      // คำนวณสถิติการจอง
      const confirmedCount = girls.length;
      const remainingCount = Math.max(0, requiredGirls - confirmedCount);
      
      setBookingStats({
        confirmed: confirmedCount,
        total: requiredGirls,
        remaining: remainingCount
      });
      
      console.log('BookedGirlDisplay: Booking stats:', {
        confirmed: confirmedCount,
        total: requiredGirls,
        remaining: remainingCount,
        bookedGirls: girls.map(g => ({ id: g.id, nickname: g.nickname }))
      });
      
    } catch (error) {
      console.error('Error fetching booked girls:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex space-x-1">
        <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse"></div>
      </div>
    );
  }

  // แสดงสถานะแม้ว่าจะยังไม่มีน้องจอง
  if (bookedGirls.length === 0 && bookingStats.confirmed === 0) {
    return (
      <div className="flex flex-col items-start space-y-2">
        <div className="text-xs text-gray-600">
          <span className="font-medium text-gray-400">0</span>
          <span className="text-gray-500">/{bookingStats.total} คน</span>
          <span className="text-orange-500 ml-1">(เหลือ {bookingStats.remaining})</span>
        </div>
        
        {/* Module สุ่มน้อง3คน - เมื่อยังไม่จองเลย */}
        <RandomGirlsModule 
          tripPlanId={tripPlanId} 
          bookedGirlIds={[]} 
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start space-y-2">
      {/* Status Message */}
      <div className="text-xs text-gray-600">
        <span className="font-medium text-green-600">{bookingStats.confirmed}</span>
        <span className="text-gray-500">/{bookingStats.total} คน</span>
        {bookingStats.remaining > 0 && (
          <span className="text-orange-500 ml-1">(เหลือ {bookingStats.remaining})</span>
        )}
      </div>
      
      {/* Girl Images */}
      <div className="flex space-x-2">
        {/* Show booked girls first */}
        {bookedGirls.slice(0, 3).map((girl) => (
          <div
            key={`booked-${girl.id}`}
            className="relative group"
            title={`${girl.nickname} - ${girl.status === 'confirmed' ? 'ยืนยันแล้ว' : 'เสร็จสิ้น'}`}
          >
            {/* Profile Image with Green Border */}
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-green-500 shadow-md">
              <img
                src={girl.image}
                alt={girl.nickname}
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Green Checkmark for confirmed girls */}
            <div className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        ))}
        
        {/* Show count if more than 3 girls */}
        {bookedGirls.length > 3 && (
          <div className="w-12 h-12 rounded-full bg-green-100 border-2 border-green-500 flex items-center justify-center text-green-600 text-xs font-bold">
            +{bookedGirls.length - 3}
          </div>
        )}
      </div>
      
      {/* Module สุ่มน้อง3คน - เมื่อยังไม่ครบตามที่ต้องการ */}
      {bookingStats.remaining > 0 && (
        <div className="mt-2">
          <RandomGirlsModule 
            tripPlanId={tripPlanId} 
            bookedGirlIds={bookedGirls.map(girl => girl.id)} 
          />
        </div>
      )}
    </div>
  );
};

export default BookedGirlDisplay;
